import {
  Collection,
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  collection,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import TournamentRegistration from "./TournamentRegistration";
import TournamentEntry from "./TournamentEntry";
import TournamentSession from "./TournamentSession";
import PlayerProfile from "./PlayerProfile";
import ClubPlayer from "./ClubPlayer";
import { isPresent, last } from "@mixitone/util";
import { setAccountId } from "./concerns/SetAccountId";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    checked_in_at: {
      type: "datetime",
    },
    club_id: {
      type: "string",
    },
    club_player_id: {
      type: "string",
    },
    confirmation_sent_at: {
      type: "datetime",
    },
    confirmed_at: {
      type: "datetime",
    },
    created_at: {
      type: "datetime",
    },
    email: {
      type: "string",
    },
    is_deleted: {
      type: "boolean",
    },
    mobile: {
      type: "string",
    },
    name: {
      type: "string",
    },
    paid_at: {
      type: "datetime",
    },
    payment_reference: {
      type: "string",
    },
    player_profile_id: {
      type: "string",
    },
    thank_you_sent_at: {
      type: "datetime",
    },
    tournament_payment_method_id: {
      type: "string",
    },
    tournament_session_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    withdrawn_at: {
      type: "datetime",
    },
  },
  {
    registrations: collection(() => TournamentRegistration as any, "tournament_player_id"),
  },
  {
    tournamentSession: reference<any>(() => TournamentSession, "tournament_session_id"),
    playerProfile: reference(() => PlayerProfile as any, "player_profile_id"),
    clubPlayer: reference(() => ClubPlayer as any, "club_player_id"),
  },
);

interface TournamentPlayer extends ModelAttributes<typeof config> {
  registrations: Collection<TournamentRegistration>;
  tournamentSession: Loadable<TournamentSession>;
  playerProfile: Loadable<PlayerProfile>;
  clubPlayer: Loadable<ClubPlayer>;
}

@setAccountId
@model("tournament_players", config)
class TournamentPlayer extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament player represents a participant in a tournament session. " +
      "They are registered to many tournament competitions and tournament entries " +
      "through tournament registrations. Players have payment status, confirmation status, " +
      "and can be checked in for events.",
    availableFields: [
      ...Object.keys(config.fields),
      "isCheckedIn",
      "isPaid",
      "isWithdrawn",
      "isConfirmed",
      "shortName",
      "shortestName",
    ],
    relationships: {
      registrations: "tournament_registrations where tournament_player_id equals this id",
      tournamentSession: "tournament_sessions where id equals this tournament_session_id",
      playerProfile: "player_profiles where id equals this player_profile_id",
      clubPlayer: "club_players where id equals this club_player_id",
    },
  };
  /** First name plus last initial, e.g. "Jeremy W". */
  get shortName() {
    if (!this.name) return "";

    const names = this.name.split(" ");
    if (names.length === 1) return names[0];

    return [names[0], last(names)!.slice(0, 1)].join(" ");
  }

  get shortestName() {
    return this.shortName;
  }

  get isCheckedIn() {
    return isPresent(this.checked_in_at);
  }

  get isPaid() {
    return isPresent(this.paid_at);
  }

  get isWithdrawn() {
    return isPresent(this.withdrawn_at);
  }

  get isConfirmed() {
    return isPresent(this.confirmed_at);
  }

  /**
   * Mark the player as having paid
   */
  async markAsPaid() {
    if (!this.isPaid) {
      this.paid_at = new Date();
      await this.save();
    }
  }

  /**
   * Clear the paid status for this player
   */
  async clearPaidStatus() {
    if (this.isPaid) {
      this.paid_at = undefined;
      await this.save();
    }
  }

  /**
   * Delete the player and any registrations. Entries are deleted when this
   * player was the last remaining player on them.
   */
  override async destroy() {
    const registrations = await TournamentRegistration.query().eq("tournament_player_id", this.id).all();
    const entryIds = [
      ...new Set(
        registrations
          .map((registration) => registration.tournament_entry_id)
          .filter((id): id is string => !!id),
      ),
    ];

    for (const registration of registrations) {
      await registration.destroy();
    }

    for (const entryId of entryIds) {
      await TournamentEntry.destroyIfNoRegistrations(entryId);
    }

    await super.destroy();
  }
}

export default TournamentPlayer;
